'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["anchorRef", "dragHandlerRef", "className"],
  _excluded2 = ["style", "anchorRef", "occurrence", "onClose", "open"],
  _excluded3 = ["children", "optionalRenderers"],
  _excluded4 = ["occurrence", "children"];
import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { EMPTY_OBJECT } from '@base-ui/utils/empty';
import Paper from '@mui/material/Paper';
import Dialog, { dialogClasses } from '@mui/material/Dialog';
import { backdropClasses } from '@mui/material/Backdrop';
import { styled, useThemeProps } from '@mui/material/styles';
import { schedulerEventSelectors, schedulerOtherSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { useSchedulerStoreContext } from '@mui/x-scheduler-internals/use-scheduler-store-context';
import { useDraggableDialog } from '@mui/x-scheduler-internals/use-draggable-dialog';
import { createModal } from "../create-modal/index.mjs";
import { FormContent } from "./FormContent.mjs";
import { calculatePosition } from "../../utils/dialog-utils.mjs";
import ReadonlyContent from "./ReadonlyContent.mjs";
import { useEventDialogStyledContext } from "./EventDialogStyledContext.mjs";
import { EventDialogOptionalRenderersContext } from "./EventDialogOptionalRenderersContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const EventDialogRoot = styled(Dialog, {
  name: 'MuiEventDialog',
  slot: 'Root'
})({
  pointerEvents: 'none',
  [`& .${backdropClasses.root}`]: {
    backgroundColor: 'transparent'
  },
  [`& .${dialogClasses.container}`]: {
    width: '100%',
    justifyContent: 'unset',
    alignItems: 'unset'
  },
  [`& .${dialogClasses.paper}`]: {
    margin: 0,
    pointerEvents: 'auto'
  }
});
const EventDialogPaper = styled(Paper, {
  name: 'MuiEventDialog',
  slot: 'Paper'
})(({
  theme
}) => ({
  borderWidth: 0,
  borderTopWidth: 1,
  height: 'fit-content',
  overflow: 'hidden',
  '&[data-dragging]': {
    outline: `1px solid ${(theme.vars || theme).palette.primary.light}`
  }
}));
// 1. Setup the Draggable Paper Logic
const PaperComponent = function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  const mutateStyle = React.useCallback(style => {
    if (nodeRef.current) {
      nodeRef.current.style.transform = style;
    }
  }, [nodeRef]);
  const {
      anchorRef,
      dragHandlerRef,
      className
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const resetDrag = useDraggableDialog(nodeRef, dragHandlerRef, mutateStyle);
  const updatePosition = React.useCallback((shouldResetDrag = false) => {
    const position = calculatePosition(anchorRef.current, nodeRef.current, 'left');
    if (position && nodeRef.current) {
      nodeRef.current.style.top = `${position.top}px`;
      nodeRef.current.style.left = `${position.left}px`;
      if (shouldResetDrag) {
        // Reset transform when position is recalculated
        resetDrag();
      }
    }
  }, [anchorRef, resetDrag, nodeRef]);
  React.useLayoutEffect(() => {
    updatePosition(true);
  }, [updatePosition]);
  React.useEffect(() => {
    const handleResize = () => {
      updatePosition(false);
      resetDrag();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updatePosition, resetDrag]);
  return /*#__PURE__*/_jsx(EventDialogPaper, _extends({}, other, {
    ref: nodeRef,
    className: className
  }));
};
if (process.env.NODE_ENV !== "production") PaperComponent.displayName = "PaperComponent";
function isFromEventDialogTrigger(event, schedulerId) {
  const selector = schedulerId != null ? `[data-event-dialog-trigger="${schedulerId}"]` : '[data-event-dialog-trigger]';
  const composedPath = event.composedPath?.();
  if (composedPath) {
    for (const node of composedPath) {
      if (!(node instanceof Element)) {
        continue;
      }
      if (node.matches(selector)) {
        return true;
      }
    }
  }
  if (event.target instanceof Element) {
    return Boolean(event.target.closest(selector));
  }
  return false;
}
const EventDialog = createModal({
  contextName: 'EventDialogContext'
});
export const EventDialogContext = EventDialog.Context;
export const useEventDialogContext = EventDialog.useContext;
export const EventDialogContent = /*#__PURE__*/React.forwardRef(function EventDialogContent(inProps, forwardedRef) {
  // eslint-disable-next-line mui/material-ui-name-matches-component-name
  const props = useThemeProps({
    props: inProps,
    name: 'MuiEventDialog'
  });
  const {
      anchorRef,
      occurrence,
      onClose,
      open
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded2);
  const dialogInstanceKey = occurrence.key ?? occurrence.id;
  // Context hooks
  const store = useSchedulerStoreContext();
  const {
    schedulerId,
    classes
  } = useEventDialogStyledContext();

  // Selector hooks
  const isEventReadOnly = useStore(store, schedulerEventSelectors.isReadOnly, occurrence.id);
  const isScopeDialogOpen = useStore(store, schedulerOtherSelectors.isRecurringScopeDialogOpen);
  const handleClickAway = React.useCallback(event => {
    // Clicking another event trigger in THIS scheduler — let EventDialogTrigger handle switching.
    // Triggers from a different scheduler instance must not suppress close.
    if (isFromEventDialogTrigger(event, schedulerId)) {
      return;
    }
    onClose();
  }, [onClose, schedulerId]);

  // Ref hooks
  const dragHandlerRef = React.useRef(null);
  return /*#__PURE__*/_jsx(ClickAwayListener, {
    onClickAway: handleClickAway,
    mouseEvent: open && !isScopeDialogOpen ? 'onClick' : false,
    touchEvent: false,
    children: /*#__PURE__*/_jsx(EventDialogRoot, _extends({
      ref: forwardedRef,
      open: open,
      onClose: onClose,
      PaperComponent: PaperComponent,
      "aria-labelledby": `${schedulerId}-event-dialog-title`,
      "aria-modal": "false",
      className: classes.eventDialog,
      slotProps: {
        paper: {
          className: classes.eventDialogPaper,
          anchorRef,
          dragHandlerRef
        }
      }
    }, other, {
      children: isEventReadOnly ? /*#__PURE__*/_jsx(ReadonlyContent, {
        occurrence: occurrence,
        onClose: onClose,
        dragHandlerRef: dragHandlerRef
      }) : /*#__PURE__*/_jsx(FormContent, {
        occurrence: occurrence,
        onClose: onClose,
        dragHandlerRef: dragHandlerRef
      })
    }), dialogInstanceKey)
  });
});
if (process.env.NODE_ENV !== "production") EventDialogContent.displayName = "EventDialogContent";
export function EventDialogProvider(props) {
  const {
      children,
      optionalRenderers
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded3);
  const store = useSchedulerStoreContext();
  const isRecurringScopeDialogOpen = useStore(store, schedulerOtherSelectors.isRecurringScopeDialogOpen);
  const showRecurrence = useStore(store, schedulerOtherSelectors.areRecurringEventsAvailable);
  const RecurringScopeDialogRenderer = optionalRenderers?.recurringScopeDialog;
  const imperativeRef = React.useRef(null);
  const lastSyncedEventId = React.useRef(null);
  const openEventId = useStore(store, schedulerOtherSelectors.openEventId);
  React.useEffect(() => {
    if (openEventId === lastSyncedEventId.current) {
      return;
    }
    lastSyncedEventId.current = openEventId;
    if (openEventId !== null) {
      const processed = store.state.processedEventLookup.get(openEventId);
      if (processed) {
        imperativeRef.current?.open(_extends({}, processed, {
          key: String(processed.id)
        }));
      }
    } else {
      imperativeRef.current?.close();
    }
  }, [openEventId, store]);
  return /*#__PURE__*/_jsx(EventDialogOptionalRenderersContext.Provider, {
    value: optionalRenderers ?? EMPTY_OBJECT,
    children: /*#__PURE__*/_jsxs(EventDialog.Provider, {
      imperativeRef: imperativeRef,
      render: ({
        isOpen,
        anchorRef,
        data: occurrence,
        onClose
      }) => /*#__PURE__*/_jsx(EventDialogContent, _extends({
        open: isOpen,
        anchorRef: anchorRef,
        occurrence: occurrence,
        onClose: onClose
      }, other)),
      onOpen: occurrence => {
        store.setEditedEventId(occurrence.id);
        lastSyncedEventId.current = occurrence.id;
        store.setOpenEventId(occurrence.id);
      },
      onClose: () => {
        store.setEditedEventId(null);
        lastSyncedEventId.current = null;
        store.setOpenEventId(null);
        store.setOccurrencePlaceholder(null);
      },
      children: [children, showRecurrence && isRecurringScopeDialogOpen && RecurringScopeDialogRenderer && /*#__PURE__*/_jsx(RecurringScopeDialogRenderer, {})]
    })
  });
}
export function EventDialogTrigger(props) {
  const {
      occurrence,
      children
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded4);
  const ref = React.useRef(null);
  const {
    schedulerId
  } = useEventDialogStyledContext();
  const annotatedChild = /*#__PURE__*/React.isValidElement(children) ? /*#__PURE__*/React.cloneElement(children, {
    'data-event-dialog-trigger': schedulerId ?? 'true'
  }) : children;
  return /*#__PURE__*/_jsx(EventDialog.Trigger, _extends({
    ref: ref,
    data: occurrence
  }, other, {
    children: annotatedChild
  }));
}